import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  commentsList,
  commentsSync,
  commentsDraftReply,
  commentsSendReply,
  commentsDelete,
  commentAgentGetSettings,
  commentAgentUpdateSettings,
} from "@/lib/social-comments.functions";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Lock, MessageSquare, RefreshCw, Sparkles, Send, Trash2, Bot } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/social/comments")({
  component: CommentsPage,
  ssr: false,
});

function CommentsPage() {
  const { isAdmin, loading } = useAdminAuth();
  const qc = useQueryClient();
  const list = useServerFn(commentsList);
  const sync = useServerFn(commentsSync);
  const draft = useServerFn(commentsDraftReply);
  const send = useServerFn(commentsSendReply);
  const del = useServerFn(commentsDelete);
  const getSettings = useServerFn(commentAgentGetSettings);
  const updateSettings = useServerFn(commentAgentUpdateSettings);

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Agent settings local form state
  const [enabled, setEnabled] = useState(false);
  const [review, setReview] = useState(true);
  const [appUrl, setAppUrl] = useState("");
  const [agentName, setAgentName] = useState("Anna");
  const [persona, setPersona] = useState("");
  const [training, setTraining] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  const commentsQ = useQuery({
    queryKey: ["social-comments"],
    queryFn: () => list(),
    enabled: isAdmin,
  });

  useQuery({
    queryKey: ["comment-agent-settings"],
    queryFn: async () => {
      const res = await getSettings();
      const s = res.settings;
      setEnabled(s.enabled);
      setReview(s.review_before_send);
      setAppUrl(s.application_url);
      setAgentName(s.agent_name);
      setPersona(s.persona);
      setTraining(s.training_notes);
      return s;
    },
    enabled: isAdmin,
  });

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-border bg-card p-10 text-center">
        <Lock className="h-6 w-6 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Admin only</h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          Only admins can manage social comments and the AI replier.
        </p>
      </div>
    );
  }

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await sync();
      if (res.inserted > 0) toast.success(`Synced ${res.inserted} new comment(s).`);
      else if (!res.supported)
        toast.info(res.note ?? "Comments aren't exposed by your GHL connection yet.");
      else toast.success("Up to date — no new comments.");
      qc.invalidateQueries({ queryKey: ["social-comments"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const doDraft = async (id: string) => {
    setBusyId(id);
    try {
      const res = await draft({ data: { id } });
      setDrafts((d) => ({ ...d, [id]: res.reply }));
      toast.success("AI reply drafted — review before sending.");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not draft a reply");
    } finally {
      setBusyId(null);
    }
  };

  const doSend = async (id: string) => {
    const msg = drafts[id]?.trim();
    if (!msg) {
      toast.error("Write or draft a reply first");
      return;
    }
    setBusyId(id);
    try {
      const res = await send({ data: { id, message: msg } });
      if (res.sent) toast.success("Reply sent.");
      else toast.success(`Reply saved.${res.note ? ` (${res.note})` : ""}`);
      qc.invalidateQueries({ queryKey: ["social-comments"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not send reply");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (id: string) => {
    setBusyId(id);
    try {
      await del({ data: { id } });
      qc.invalidateQueries({ queryKey: ["social-comments"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateSettings({
        data: {
          enabled,
          review_before_send: review,
          application_url: appUrl,
          agent_name: agentName,
          persona,
          training_notes: training,
        },
      });
      toast.success("Agent settings saved.");
      qc.invalidateQueries({ queryKey: ["comment-agent-settings"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const comments = commentsQ.data?.comments ?? [];

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Comments</h2>
          </div>
          <Button variant="outline" size="sm" onClick={runSync} disabled={syncing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync comments"}
          </Button>
        </div>

        {commentsQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {comments.length === 0 && !commentsQ.isLoading && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No comments yet. Click <strong>Sync comments</strong> to pull the latest from your
            connected accounts. If your GHL connection doesn&apos;t expose comments, they&apos;ll
            need to be managed in the GHL Social Planner.
          </div>
        )}

        <div className="space-y-3">
          {comments.map((c: any) => {
            const value = drafts[c.id] ?? c.replied_message ?? c.ai_suggested_reply ?? "";
            const isBusy = busyId === c.id;
            return (
              <div key={c.id} className="rounded-lg border border-border bg-card p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-semibold">{c.author_name ?? "Follower"}</span>{" "}
                    {c.platform && (
                      <span className="text-xs uppercase text-muted-foreground">{c.platform}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {c.status === "replied" && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                        Replied
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => doDelete(c.id)}
                      disabled={isBusy}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-sm">{c.message}</p>
                {c.comment_at && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {new Date(c.comment_at).toLocaleString()}
                  </p>
                )}

                <div className="mt-3 space-y-2">
                  <Textarea
                    rows={2}
                    value={value}
                    onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                    placeholder="Write a reply, or let AI draft one…"
                    maxLength={2000}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => doDraft(c.id)}
                      disabled={isBusy}
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      AI draft
                    </Button>
                    <Button size="sm" onClick={() => doSend(c.id)} disabled={isBusy}>
                      <Send className="mr-2 h-4 w-4" />
                      {isBusy ? "Working…" : "Send reply"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <aside className="space-y-4 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <h3 className="text-sm font-semibold">AI comment agent</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          A sales-focused, compliance-safe assistant that drafts on-brand replies. It never promises
          approval, rates, or funding amounts, and never argues.
        </p>

        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Enable AI replier</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </label>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Review before sending</span>
          <Switch checked={review} onCheckedChange={setReview} />
        </label>

        <div>
          <Label htmlFor="agentName">Agent name</Label>
          <Input
            id="agentName"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder="Anna"
            maxLength={80}
          />
        </div>
        <div>
          <Label htmlFor="appUrl">Application URL</Label>
          <Input
            id="appUrl"
            value={appUrl}
            onChange={(e) => setAppUrl(e.target.value)}
            placeholder="https://…/apply"
            maxLength={2000}
          />
        </div>
        <div>
          <Label htmlFor="persona">Persona / voice</Label>
          <Textarea
            id="persona"
            rows={8}
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            placeholder="Warm, knowledgeable advisor; concise; encouraging."
            maxLength={12000}
          />
        </div>
        <div>
          <Label htmlFor="training">Training notes</Label>
          <Textarea
            id="training"
            rows={10}
            value={training}
            onChange={(e) => setTraining(e.target.value)}
            placeholder="Key facts, dos and don'ts, common questions and the best answers…"
            maxLength={20000}
          />
        </div>

        <Button onClick={saveSettings} disabled={savingSettings} className="w-full">
          {savingSettings ? "Saving…" : "Save agent settings"}
        </Button>
      </aside>
    </div>
  );
}
